import { CountryCodeType } from '../App/types'
import { ApiKeyCredentialsType, ApiKeyType } from '../Form/ApiKey/types'
import { SymbolType } from '../Instrument/types'
import {
  RejectedMessageType,
  SubscribedMessageType,
  UnsubscribedMessageType
} from '../Socket/types'

export * from './ExtraKYCQuestions/types'

export type UserSettingsType = {
  favorites?: string
  localCurrency?: string
  showNotifications?: boolean
}

export enum TierType {
  New = 0,
  Silver = 1,
  SilverPlus = 3,
  SilverPlusPlus = 4,
  Gold = 2,
  Platinum = 5
}

export type UserTiersType = {
  current: TierType
  next: TierType
  selected: TierType
}

export type LimitDefinitionType = {
  day: number
  week: number
  year: number
}

export type KycStateType = 'NONE' | 'PENDING' | 'UNDER_REVIEW' | 'REJECTED' | 'VERIFIED' | 'EXPIRED'

export type WalletAddressType = {
  [currency: string]: string
}

export type LocalSettingsType = {
  orderFilledSound: boolean
}

export type UserAddress = {
  city: string
  country: CountryCodeType
  line1: string
  line2: string
  postCode: string
  state: string
}

export type UserState = 'NONE' | 'CREATED' | 'ACTIVE' | 'BLOCKED'

export type UserType = {
  address: UserAddress
  dob?: string
  email: string
  emailVerified: boolean
  firstName?: string
  id: string
  kycState: KycStateType
  lastName?: string
  mercuryEmailVerified?: boolean
  mobile: string
  mobileVerified: boolean
  resubmission?: {
    details: string
    reason: number
  }
  settings?: UserSettingsType
  ssnVerified?: boolean
  state: UserState
  tags?: {
    AFFILIATE?: {
      MERCURY_AFFILIATE_USER_ENABLED?: boolean
    }
  }
  tiers: UserTiersType
  unifiedAccountWalletGuid?: string
  userName?: string
  walletAddresses?: WalletAddressType
  walletGuid?: string
}

export type TwoFAType =
  | 'TOTP' // 2fa app like google auth. Deprecated
  | 'BACKUP' // backup codes. Deprecated
  | 'WALLET_STANDARD' // no 2FA
  | 'WALLET_YUBIKEY' // yubikey configured in the wallet
  | 'WALLET_EMAIL' // email to wallet configured email. Deprecated
  | 'WALLET_YUBIKEY_MTGOX' // Yubikey from Mt.Gox era
  | 'WALLET_GOOGLE_AUTH' // 2fa app like google auth configured in the wallet
  | 'WALLET_SMS' // SMS to cellphone number configured on the wallet

export type SecurityEventTypeType = 'signin' | 'password' | 'twofactor' | 'email'

export type SecurityEventType = {
  location: string
  timestamp: number
  type: SecurityEventTypeType
}

export type DepositResponseType = {
  address: string
  id: string
}

export type WithdrawalPurposeType = 'WITHDRAWAL_FIAT' | 'WITHDRAWAL_CRYPTO'
export type WithdrawalPeriodType = 'DAY' | 'WEEK' | 'MONTH' | 'YEAR'

export type LimitType = {
  limit: number
  used: number
}

export type LimitsType = {
  [withdrawalPurpose: string]: {
    [withdrawalPeriod: string]: LimitType
  }
}

export type TierDefinitionType = {
  [key: number]: {
    [withdrawalPurpose: string]: {
      [withdrawalPeriod: string]: number
    }
  }
}

export type TiersLimitsVolumeType = {
  limits: LimitsType
  tiers: TierDefinitionType
  volume: number
}

export type WithdrawalLimitResponseType = {
  currency: string
  purpose: WithdrawalPurposeType
  quantity: number
  tier: number
  type: WithdrawalPeriodType
}

export type FeeType = {
  expires?: string
  makerFee: number
  makerMin: number
  name: string
  overrideFromDefault: boolean
  takerFee: number
  takerMin: number
}

export type SymbolFeeType = {
  active: FeeType
  pending?: FeeType
}

export type UserFeesType = {
  [key: string]: SymbolFeeType
}

export type AccumulatorType = {
  amount: { symbol: SymbolType; value: string }
  product: string
  txType: string
  type: 'DAY' | 'WEEK' | 'MONTH' | 'YEAR'
}

export type FeesResponseType = {
  fees: UserFeesType
  feesAccumulators: Array<AccumulatorType>
}

export type AccumulatorResponseType = {
  amount: {
    symbol: string
    value: number
  }
  isFiat: boolean
  type: WithdrawalPeriodType
}

export type TiersLimitsVolumeResponseType = {
  ordersEventsAccumulators?: Array<AccumulatorResponseType>
  withdrawalAccumulators: Array<AccumulatorResponseType>
  withdrawalLimits?: Array<WithdrawalLimitResponseType>
}

export type AuthMessageType = RejectedMessageType | SubscribedMessageType | UnsubscribedMessageType

export type ApiSettingsType = {
  keyToDeactivate: string
  keys: Array<ApiKeyType>
  newKey: ApiKeyCredentialsType
}

export type DeviceCheckBodyType = {
  approve?: boolean
  code?: string
}

export type DeviceInfoType = {
  address: string
  browser: string
  location: string
  requestAddress: string
  requestBrowser: string
  requestLocation: string
}
